import { Boxes } from 'lucide-react';

import { Modal } from '@@/modals';
import { Button } from '@@/buttons';
import { Alert } from '@@/Alert';
import { Loading } from '@@/Widget';
import { Badge } from '@@/Badge';

import { useDeploymentResources } from '../queries/useDeploymentResources';

/**
 * Lists what a reconciled deployment declares.
 *
 * The list comes from the deployment's manifests, so it is the same for every
 * environment running it. Which of them is not working, or has been changed on
 * the cluster, is what the status table says per environment.
 */
export function ResourcesModal({
  workflowId,
  deploymentId,
  name,
  onDismiss,
}: {
  workflowId: number;
  deploymentId: number;
  name: string;
  onDismiss: () => void;
}) {
  const { data, isLoading, isError } = useDeploymentResources(
    workflowId,
    deploymentId,
    { enabled: true }
  );

  return (
    <Modal onDismiss={onDismiss} aria-label={`Resources of ${name}`} size="lg">
      <Modal.Header title="Deployment resources" />
      <Modal.Body>
        {isLoading && <Loading />}

        {isError && (
          <Alert color="error">
            Failed loading the resources of this deployment.
          </Alert>
        )}

        {!isLoading && !isError && !data?.length && (
          <Alert color="info">
            Open an environment&apos;s resource count to see the resources this
            chart rendered there.
          </Alert>
        )}

        {!!data?.length && (
          <div className="max-h-96 overflow-y-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Name</th>
                  <th>Namespace</th>
                </tr>
              </thead>
              <tbody>
                {data.map((resource) => (
                  <tr
                    key={`${resource.apiVersion}/${resource.kind}/${resource.namespace}/${resource.name}`}
                  >
                    <td>
                      <div className="flex items-center gap-2">
                        <Badge type="muted">{resource.kind}</Badge>
                        <span className="text-muted text-xs">
                          {resource.apiVersion}
                        </span>
                      </div>
                    </td>
                    <td className="font-medium">{resource.name}</td>
                    <td className="text-muted">{resource.namespace || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onDismiss} color="secondary" data-cy="close-resources">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** The count in the status table, which opens the list when clicked. */
export function ResourceCountButton({
  count,
  onClick,
  index,
}: {
  count: number;
  onClick: () => void;
  index: number;
}) {
  return (
    <Button
      color="link"
      icon={Boxes}
      onClick={onClick}
      className="!p-0"
      data-cy={`workflow-reconcile-resources-${index}`}
    >
      {count} resources
    </Button>
  );
}
